import React, { useState, useEffect } from "react";
import {
	Card,
	Form,
	Select,
	Button,
	Table,
	DatePicker,
	Switch,
	InputNumber,
	Space,
	Typography,
	Alert,
	message,
} from "antd";
import {
	PlusOutlined,
	UserOutlined,
	SaveOutlined,
	ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import { toKoreaApiDateTime } from "../../utilities/koreaTime";
import SearchMemberModal from "../member/SearchMemberModal";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { Option } = Select;

const AddBulkEnrollmentsPage = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [courses, setCourses] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [showMemberModal, setShowMemberModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		fetchCourses();
	}, []);

	const fetchCourses = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get("/cma/courses/");
			setCourses(response.data);
		} catch (error) {
			console.error("Error fetching courses:", error);
			message.error("강의 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleMemberSelect = (member) => {
		// Check if member is already selected
		if (!selectedUsers.find((u) => u.id === member.id)) {
			setSelectedUsers([...selectedUsers, member]);
		} else {
			message.warning("이미 선택된 회원입니다.");
		}
	};

	const handleRemoveUser = (userId) => {
		setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
	};

	const handleSubmit = async (values) => {
		if (selectedUsers.length === 0) {
			message.error("최소 한 명의 회원을 선택해주세요.");
			return;
		}

		try {
			setSubmitting(true);
			const payload = {
				course_id: values.course_id,
				user_ids: selectedUsers.map((u) => u.id),
				is_paid: values.is_paid || false,
				payment_date: toKoreaApiDateTime(
					values.payment_date || dayjs(),
				),
				payment_amount: values.payment_amount || 0,
				result: values.result || "미수료",
			};

			const response = await useAxios.post(
				"/cma/admin/enrollments/bulk/",
				payload
			);

			if (response.data.success) {
				message.success(
					`${response.data.created_count}명의 수강 신청이 완료되었습니다.`
				);
				if (response.data.skipped_count > 0) {
					message.warning(
						`${response.data.skipped_count}명은 이미 수강 신청되어 있어 건너뛰었습니다.`
					);
				}
				// Reset form
				form.resetFields();
				setSelectedUsers([]);
			}
		} catch (error) {
			console.error("Error creating enrollments:", error);
			message.error(
				error.response?.data?.error ||
					"수강 신청을 생성하는데 실패했습니다."
			);
		} finally {
			setSubmitting(false);
		}
	};

	const userColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "연락처",
			dataIndex: "phone_number",
			key: "phone_number",
			render: (text) => text || "-",
		},
		{
			title: "소속",
			key: "affiliated_group",
			render: (_, record) =>
				record.cell_name ||
				record.serving_department_name ||
				record.affiliated_department_name ||
				"-",
		},
		{
			title: "작업",
			key: "action",
			render: (_, record) => (
				<Button
					type="link"
					danger
					onClick={() =>
						handleRemoveUser(record.id)
					}>
					제거
				</Button>
			),
		},
	];

	return (
		<div style={{ padding: "24px" }}>
			<PageTitle title="기독사관학교 - 다중 수강 신청 등록" />
			<Card
				style={{ marginBottom: "24px" }}
				title={
					<Space>
						<UserOutlined />
						<Title
							level={4}
							style={{ margin: 0 }}>
							다중 수강 신청
						</Title>
					</Space>
				}
				extra={
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() =>
							navigate("/cma/admin")
						}>
						관리자 페이지로
					</Button>
				}>
				<Alert
					message="안내"
					description="여러 회원을 선택하여 한 번에 수강 신청을 등록할 수 있습니다."
					type="info"
					showIcon
					style={{ marginBottom: "24px" }}
				/>

				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						is_paid: false,
						payment_amount: 0,
						result: "미수료",
						payment_date: dayjs(),
					}}>
					<Form.Item
						label="강의 선택"
						name="course_id"
						rules={[
							{
								required: true,
								message: "강의를 선택해주세요.",
							},
						]}>
						<Select
							placeholder="강의를 선택하세요"
							loading={loading}
							showSearch
							filterOption={(
								input,
								option
							) =>
								(
									option?.children ??
									""
								)
									.toLowerCase()
									.includes(
										input.toLowerCase()
									)
							}>
							{courses.map(
								(course) => (
									<Option
										key={
											course.id
										}
										value={
											course.id
										}>
										{
											course.name
										}{" "}
										(
										{
											course
												.book
												?.title
										}

										)
									</Option>
								)
							)}
						</Select>
					</Form.Item>

					<Form.Item label="회원 선택">
						<Space
							direction="vertical"
							style={{
								width: "100%",
							}}>
							<Button
								type="dashed"
								icon={
									<PlusOutlined />
								}
								onClick={() =>
									setShowMemberModal(
										true
									)
								}
								block>
								회원 추가
							</Button>
							{selectedUsers.length >
								0 && (
								<>
									<Text type="secondary">
										선택된
										회원:{" "}
										{
											selectedUsers.length
										}

										명
									</Text>
									<Table
										columns={
											userColumns
										}
										dataSource={
											selectedUsers
										}
										rowKey="id"
										pagination={
											false
										}
										size="small"
									/>
								</>
							)}
						</Space>
					</Form.Item>

					<Form.Item
						label="결제 여부"
						name="is_paid"
						valuePropName="checked">
						<Switch />
					</Form.Item>

					<Form.Item
						label="결제 금액"
						name="payment_amount"
						rules={[
							{
								required: true,
								message: "결제 금액을 입력해주세요.",
							},
						]}>
						<InputNumber
							style={{
								width: "100%",
							}}
							min={0}
							formatter={(value) =>
								`${value}`.replace(
									/\B(?=(\d{3})+(?!\d))/g,
									","
								)
							}
							parser={(value) =>
								value.replace(
									/\$\s?|(,*)/g,
									""
								)
							}
						/>
					</Form.Item>

					<Form.Item
						label="결제 일자"
						name="payment_date"
						rules={[
							{
								required: true,
								message: "결제 일자를 선택해주세요.",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
							format="YYYY-MM-DD"
						/>
					</Form.Item>

					<Form.Item
						label="수료 상태"
						name="result">
						<Select>
							<Option value="미수료">
								미수료
							</Option>
							<Option value="수료">
								수료
							</Option>
						</Select>
					</Form.Item>

					<Form.Item>
						<Space>
							<Button
								type="primary"
								htmlType="submit"
								icon={
									<SaveOutlined />
								}
								loading={
									submitting
								}
								size="large">
								수강 신청 등록
							</Button>
							<Button
								onClick={() => {
									form.resetFields();
									setSelectedUsers(
										[]
									);
								}}>
								초기화
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Card>

			<SearchMemberModal
				visible={showMemberModal}
				onCancel={() => setShowMemberModal(false)}
				onSelect={handleMemberSelect}
				apiPath="/users/cma-members/"
			/>
		</div>
	);
};

export default AddBulkEnrollmentsPage;
